import type { HTMLReactParserOptions } from 'html-react-parser';
import Parser from 'html-react-parser';
import React, { Children } from 'react';

import { isBrowser } from './web';

export const ONCE_LOADED_ACTIONS_NAME = `__gc__once__loaded__actions__`;

export const onLoadPromise = new Promise<boolean>(res => {
  if (!isBrowser) return;

  window.onload = () => setTimeout(() => res(true));
});

(async () => {
  await onLoadPromise;

  if (!isBrowser) return;

  (window as any)[ONCE_LOADED_ACTIONS_NAME] = (window as any)[ONCE_LOADED_ACTIONS_NAME] || [];

  (window as any)[ONCE_LOADED_ACTIONS_NAME].forEach((fn: () => void) => fn());
})();

/**
 * Prints HTML directly inside jsx (same as dangerouslySetHtml)
 * dangerous: do not use it on user submitted data
 */
export const html = (
  htmlStr: string,
  opts: HTMLReactParserOptions = { htmlparser2: { decodeEntities: true } },
) => Parser(htmlStr || '', opts);

/**
 * Strip Html from a string, usage:
 *
 * stripHtml('<b>foo</b>')
 * => foo
 */
export const stripHtml = (str?: string | null) =>
  str
    ? str
        .replace(/<(p)>(?!<\/p>)(.*?)<\/\1>/g, '$2<br />')
        .replace(/<br\s?\/?>/g, '\n')
        .replace(/<\/[^>]*><[^>]*>/g, ' ')
        .replace(/<[^>]*>?/gm, '')
        // remove multiple spaces
        .replace(/ +(?= )/g, '')
        .trim()
    : '';

const NAMED_ENTITIES: Record<string, string> = {
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  amp: '&',
};

/**
 * Une entité numérique hors plage Unicode (ou un surrogate isolé) fait THROW `String.fromCodePoint`
 * (`RangeError: Invalid code point`) — vérifié sur `&#1114112;` et `&#x110000;`. Le corpus visé est
 * du legacy 2006-2011 (entités malformées) et ce code tourne dans le corps de `ThreadPage` : un throw
 * = régénération ISR en échec / 500 sur le fil. Hors plage → l'entité est laissée telle quelle.
 */
const safeCodePoint = (cp: number, raw: string): string =>
  Number.isFinite(cp) && cp >= 0 && cp <= 0x10ffff && !(cp >= 0xd800 && cp <= 0xdfff)
    ? String.fromCodePoint(cp)
    : raw;

/**
 * Décode les entités HTML — UNE seule passe (le legacy est échappé une fois en base).
 * ⚠ Numériques ET nommées dans la MÊME alternation, un SEUL `replace` : un `&` produit par le
 * décodage n'est jamais ré-examiné. Trois `replace` enchaînés (numérique, numérique, nommé) ne
 * protègent QUE le chemin nommé : `&#38;lt;` deviendrait `&` puis `<` — double décodage.
 * ⚠ `ENTITY_RE` et `BLOCK_TAGS` sont des regex `g` au scope module : sûres avec `.replace()` (qui
 * remet `lastIndex` à 0), piégeuses avec `.test()`/`.exec()` — état partagé entre deux appels.
 */
const ENTITY_RE = /&(?:#x([0-9a-f]+)|#(\d+)|(lt|gt|quot|apos|nbsp|amp));/gi;

const decodeEntitiesOnce = (s: string): string =>
  s.replace(ENTITY_RE, (m, hex, dec, name) =>
    hex !== undefined
      ? safeCodePoint(parseInt(hex, 16), m)
      : dec !== undefined
      ? safeCodePoint(Number(dec), m)
      : NAMED_ENTITIES[name.toLowerCase()],
  );

/** Balises de bloc : leur disparition doit laisser une frontière de mot. */
const BLOCK_TAGS =
  /<\/?(br|p|div|li|tr|td|th|h[1-6]|ul|ol|table|tbody|thead|blockquote|hr|pre|section|article)(\s[^>]*)?\/?>/gi;

/**
 * HTML (réel ou échappé une fois) → texte plat, pour meta description / JSON-LD (B1_03, contrat C2).
 * Ne remplace PAS `stripHtml` : il retire les balises avec une regex plus stricte, qui préserve un
 * `<` non suivi d'un nom de balise refermé par `>`, décode les entités (une passe), retire les
 * jetons d'import `{mot}` (D13) et normalise les blancs.
 */
export const toPlainText = (str?: string | null): string => {
  if (!str) return '';
  let s = decodeEntitiesOnce(str); // 1. une passe, jamais deux
  s = s
    .replace(/<!--[\s\S]*?-->/g, ' ') // 2a. commentaires HTML
    .replace(BLOCK_TAGS, ' ') // 2b. balises de BLOC → espace
    .replace(/<\/?[a-z][a-z0-9-]*(\s[^>]*)?\/?>/gi, ''); // 2c. balises INLINE nommées → rien
  s = s.replace(/\{[a-z0-9_]+\}/gi, ''); // 3. jetons d'import (D13)
  return s.replace(/\s+/g, ' ').trim(); // 4. blancs normalisés
};

// Preserve line breaks: convert \n to <br />
export function textToHtml<T extends string | undefined>(
  str: T,
): T extends string ? string : undefined {
  if (!str) return str as unknown as T extends string ? string : undefined;
  return (str as string).replaceAll('\n', '<br />') as T extends string ? string : undefined;
}

export type ScrollAnchorArgType = {
  anchorName: string;
  options?: boolean | ScrollIntoViewOptions;
};

export const scrollToAnchor = ({ anchorName, options }: ScrollAnchorArgType) => {
  const doScroll = () => {
    const elt = document.querySelector(`#${anchorName}`);
    if (!elt) return;

    elt.scrollIntoView(options);
  };

  // With `options: { behavior: 'smooth' }`, sometimes, there is no scroll without the setTimeout, I don't know why.
  // The setTimeout is a hack, but it seems to work. To retest, the behavior changes over time (browsers updates?).
  // Also, Firefox and Safari first set the scroll to a weird, incorrect position, that overrides the immediate scroll above. I don't know why. As a workaround, I force re-set the scroll at the expected position.
  doScroll();
  setTimeout(doScroll, 100);
};

export function scrollBottomReached(elt: Element) {
  if (!elt) {
    throw new Error('No scrolling element to check scroll bottom reached.');
  }
  const { scrollTop, scrollHeight, clientHeight } = elt;

  // `<= 3` because `scrollTop`, `clientHeight` and `scrollHeight` are all rounded, which could lead to
  // a difference of 3 in the worst case, when the bottom is reached.
  // https://stackoverflow.com/questions/3898130/check-if-a-user-has-scrolled-to-the-bottom-not-just-the-window-but-any-element#comment92747215_34550171
  return Math.abs(scrollTop + clientHeight - scrollHeight) <= 3;
}

export const scriptLoader = async ({
  id,
  async = true,
  defer = true,
  beforeOnload,
  onload,
  attributes = {},
  src,
  ...other
}: {
  beforeOnload?: boolean;
  async?: boolean;
  defer?: boolean;
  src?: string;
  attributes?: Record<string, string>;
  onload?: (event: Event) => void;
  id?: string;
}) => {
  if (!beforeOnload) {
    await onLoadPromise;
  }
  if (id && document.getElementById(id)) return true;
  const script = document.createElement('script');
  script.id = id as string;
  script.type = 'text/javascript';
  script.async = async;
  script.defer = defer;

  Object.entries(attributes).forEach(([key, val]) => script.setAttribute(key, val));
  Object.entries(other).forEach(([key, val]) => script.setAttribute(key, val as any));

  const resultPromise = src
    ? new Promise<boolean>(res => {
        script.addEventListener(
          'load',
          (...arg) => {
            if (typeof onload === 'function') onload(...arg);
            res(true);
          },
          false,
        );
      })
    : true;

  if (src) script.src = src;
  document.body.appendChild(script);
  return resultPromise;
};

export const nest = (props: React.PropsWithChildren<unknown>) => (
  <>
    {Children.toArray(props.children)
      .reverse()
      .reduce(
        (child, parent) =>
          React.cloneElement(
            parent as React.ReactElement,
            (parent as React.ReactElement).props,
            child,
          ),
        null as React.ReactNode,
      )}
  </>
);

export const isClickAwayFromElement = (clickEvent: MouseEvent, element: HTMLElement) => {
  const { clientX, clientY } = clickEvent;
  const { top, bottom, right, left } = element.getBoundingClientRect();

  if (clientX < left || clientX > right || clientY < top || clientY > bottom) {
    return true;
  }
  return false;
};
